import React, { useState, useEffect } from 'react';
import { Button, Container, Text, TextInput } from 'components';
import { FiMail, FiShoppingBag, FiUser, FiInfo, FiMapPin } from 'react-icons/fi';
import { InviteData, RetailerOption, APISyncOption } from './types';
import CustomDropdown from './CustomDropdown';
import StyledInviteForm from './invite-form-styled';

interface InviteFormProps {
  onSubmit: (inviteData: Omit<InviteData, 'id' | 'invitedAt' | 'status' | 'inviteLink'>) => Promise<void>;
  isLoading: boolean;
  userRole: 'brand' | 'customer_success';
  brandName?: string;
  selectedStateCode?: string;
  onStateChange?: (stateCode: string) => void;
  states?: Array<{
    stateCode: string;
    stateName: string;
  }>;
  retailers?: Array<{
    existsInVmiq: boolean;
    pistilRetailerId: string;
    stateCode: string;
    storeName: string;
  }>;
  apiSynced?: Array<{
    existsInVmiq: boolean;
    pistilRetailerId: string;
    stateCode: string;
    storeName: string;
  }>;
}



const InviteForm: React.FC<InviteFormProps> = ({
  onSubmit,
  isLoading,
  userRole,
  brandName,
  selectedStateCode,
  onStateChange,
  states = [],
  retailers = [],
  apiSynced = []
}) => {
  const [storeType, setStoreType] = useState<'retailer' | 'api_sync' | ''>('');
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [retailerEmail, setRetailerEmail] = useState('');
  const [retailerName, setRetailerName] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const getStateName = (stateCode: string): string => {
    const stateMap: Record<string, string> = {
      'AL': 'Alabama',
      'AK': 'Alaska',
      'AZ': 'Arizona',
      'AR': 'Arkansas',
      'CA': 'California',
      'CO': 'Colorado',
      'CT': 'Connecticut',
      'DE': 'Delaware',
      'FL': 'Florida',
      'GA': 'Georgia',
      'HI': 'Hawaii',
      'ID': 'Idaho',
      'IL': 'Illinois',
      'IN': 'Indiana',
      'IA': 'Iowa',
      'KS': 'Kansas',
      'KY': 'Kentucky',
      'LA': 'Louisiana',
      'ME': 'Maine',
      'MD': 'Maryland',
      'MA': 'Massachusetts',
      'MI': 'Michigan',
      'MN': 'Minnesota',
      'MS': 'Mississippi',
      'MO': 'Missouri',
      'MT': 'Montana',
      'NE': 'Nebraska',
      'NV': 'Nevada',
      'NH': 'New Hampshire',
      'NJ': 'New Jersey',
      'NM': 'New Mexico',
      'NY': 'New York',
      'NC': 'North Carolina',
      'ND': 'North Dakota',
      'OH': 'Ohio',
      'OK': 'Oklahoma',
      'OR': 'Oregon',
      'PA': 'Pennsylvania',
      'RI': 'Rhode Island',
      'SC': 'South Carolina',
      'SD': 'South Dakota',
      'TN': 'Tennessee',
      'TX': 'Texas',
      'UT': 'Utah',
      'VT': 'Vermont',
      'VA': 'Virginia',
      'WA': 'Washington',
      'WV': 'West Virginia',
      'WI': 'Wisconsin',
      'WY': 'Wyoming',
      'DC': 'District of Columbia',
      
      'AB': 'Alberta',
      'BC': 'British Columbia',
      'MB': 'Manitoba',
      'NB': 'New Brunswick',
      'NL': 'Newfoundland and Labrador',
      'NS': 'Nova Scotia',
      'ON': 'Ontario',
      'PE': 'Prince Edward Island',
      'QC': 'Quebec',
      'SK': 'Saskatchewan',
      'NT': 'Northwest Territories',
      'NU': 'Nunavut',
      'YT': 'Yukon'
    };
    
    return stateMap[stateCode] || stateCode;
  };

  const retailerOptions: RetailerOption[] = retailers.map(retailer => ({
    label: retailer.storeName,
    value: retailer.pistilRetailerId,
    email: '', 
    isVMIQ: retailer.existsInVmiq
  }));

  const apiSyncOptions: APISyncOption[] = apiSynced.map(store => ({
    label: store.storeName,
    value: store.pistilRetailerId,
    email: '',
    posSystem: 'Unknown'
  }));

  const brandOptions = [
    { label: 'Brand A', value: 'brand_a' },
    { label: 'Brand B', value: 'brand_b' },
    { label: 'Brand C', value: 'brand_c' }
  ];

  useEffect(() => {
    if (selectedStore && storeType) {
      if (storeType === 'retailer') {
        const retailer = retailerOptions.find(r => r.value === selectedStore);
        if (retailer?.email) {
          setRetailerEmail(retailer.email);
          setRetailerName(retailer.label);
        }
      } else if (storeType === 'api_sync') {
        const apiSync = apiSyncOptions.find(a => a.value === selectedStore);
        if (apiSync?.email) {
          setRetailerEmail(apiSync.email);
          setRetailerName(apiSync.label);
        }
      }
    } else {
      setRetailerEmail('');
      setRetailerName('');
    }
  }, [selectedStore, storeType]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (userRole === 'customer_success' && !selectedBrand) {
      newErrors.brand = 'You must select a brand';
    }

    if (!storeType) {
      newErrors.storeType = 'You must select a store type';
    }

    if (!selectedStore) {
      newErrors.store = 'You must select a store';
    }

    if (storeType !== 'api_sync') {
      if (!retailerEmail) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(retailerEmail)) {
        newErrors.email = 'Email is not valid';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const inviteData: Omit<InviteData, 'id' | 'invitedAt' | 'status' | 'inviteLink'> = {
      retailerName: retailerName || 
        (storeType === 'retailer' ? retailerOptions.find(r => r.value === selectedStore)?.label : '') ||
        (storeType === 'api_sync' ? apiSyncOptions.find(a => a.value === selectedStore)?.label : '') || '',
      retailerEmail: storeType === 'api_sync' ? '' : retailerEmail, 
      retailerId: selectedStore, 
      invitedBy: userRole === 'brand' ? 'Brand Manager' : 'Customer Success',
      brandName: userRole === 'customer_success' ? brandOptions.find(b => b.value === selectedBrand)?.label : brandName
    };

    await onSubmit(inviteData);
  };

  const handleStoreTypeChange = (type: 'retailer' | 'api_sync') => {
    setStoreType(type);
    setSelectedStore(''); 
    setErrors(prev => ({ ...prev, storeType: '', store: '' }));
  };

  const handleStoreChange = (value: string) => {
    setSelectedStore(value);
    setErrors(prev => ({ ...prev, store: '' }));
  };


  const handleEmailChange = (value: string) => {
    setRetailerEmail(value);
    setErrors(prev => ({ ...prev, email: '' }));
  };

  const handleNameChange = (value: string) => {
    setRetailerName(value);
    setErrors(prev => ({ ...prev, name: '' }));
  };

  const handleBrandChange = (value: string) => {
    setSelectedBrand(value);
    setErrors(prev => ({ ...prev, brand: '' }));
  };

  return (
    <StyledInviteForm className="pos-invite-form">
      <form onSubmit={handleSubmit}>
                {userRole === 'customer_success' && (
          <div className="form-group">
            <div className="form-label">
              <Text textStyle="s2" color="text">
                Select Brand
              </Text>
            </div>
            <select
              value={selectedBrand}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="form-select"
            >
              <option value="">Select a brand</option>
              {brandOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.brand && (
              <div className="error-message">
                <Text textStyle="support" color="error">
                  {errors.brand}
                </Text>
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <div className="form-label">
            <Text textStyle="s2" color="text">
              Select Store Type
            </Text>
          </div>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="storeType"
                value="retailer"
                checked={storeType === 'retailer'}
                onChange={() => handleStoreTypeChange('retailer')}
                className="radio-input"
              />
              <span className="radio-label">
                <Text textStyle="s2" color="text">
                  Retailer Store
                </Text>
                <Text textStyle="support" color="text">
                  Select from existing stores
                </Text>
              </span>
            </label>
            
            <label className="radio-option">
              <input
                type="radio"
                name="storeType"
                value="api_sync"
                checked={storeType === 'api_sync'}
                onChange={() => handleStoreTypeChange('api_sync')}
                className="radio-input"
              />
              <span className="radio-label">
                <Text textStyle="s2" color="text">
                  Already Connected
                </Text>
                <Text textStyle="support" color="text">
                  View retailers who completed the invitation process
                </Text>
              </span>
            </label>
          </div>
          {errors.storeType && (
            <div className="error-message">
              <Text textStyle="support" color="error">
                {errors.storeType}
              </Text>
            </div>
          )}
        </div>

                 <div className="form-group">
           <div className="form-row">
             <div className="form-field">
               <label className="field-label">
                 {storeType ? (
                   storeType === 'retailer' ? (
                      <div className="state-label-container">
                        <Text textStyle="s2" color="text">
                          Select Store in
                        </Text>
                        <span className="state-badge">
                          <FiMapPin className="state-icon" />
                          <select
                            value={selectedStateCode || ''}
                            onChange={(e) => onStateChange?.(e.target.value)}
                            className="state-badge-select"
                          >
                            <option value="">Select state</option>
                            {states.map(state => (
                              <option key={state.stateCode} value={state.stateCode}>
                                {getStateName(state.stateCode)}
                              </option>
                            ))}
                          </select>
                        </span>
                      </div>
                   ) : (
                     <Text textStyle="s2" color="text">
                       View Connected Stores
                     </Text>
                   )
                 ) : (
                   <Text textStyle="s2" color="text">
                     Select Store
                   </Text>
                 )}
               </label>
               <CustomDropdown
                 options={storeType === 'retailer' ? retailerOptions : storeType === 'api_sync' ? apiSyncOptions : []}
                 value={selectedStore}
                 onChange={handleStoreChange}
                 placeholder={storeType 
                   ? (storeType === 'retailer' ? (selectedStateCode ? 'Select a Store' : 'First select a state above') : 'View a Store')
                   : 'First select a store type above'
                 }
                 disabled={!storeType || (storeType === 'retailer' && !selectedStateCode)}
                 className="custom-dropdown-field"
                 searchable={storeType === 'retailer'}
               />
               {errors.store && (
                 <div className="field-error">
                   <Text textStyle="support" color="error">
                     {errors.store}
                   </Text>
                 </div>
               )}
             </div>
             
             {storeType !== 'api_sync' && (
               <div className="form-field">
                 <label className="field-label">
                   <Text textStyle="s2" color="text">
                     Email
                   </Text>
                 </label>
                 <input
                   value={retailerEmail}
                   onChange={(e) => handleEmailChange(e.target.value)}
                   placeholder="example@retailer.com"
                   type="email"
                   disabled={!selectedStore}
                   className="field-input"
                 />
                 {errors.email && (
                   <div className="field-error">
                     <Text textStyle="support" color="error">
                       {errors.email}
                     </Text>
                   </div>
                 )}
               </div>
             )}
           </div>
         </div>

         <div className="form-info-simple">
           <div className="info-message">
             <FiInfo className="info-icon" />
             <Text textStyle="support" color="text">
               If you can't find your store, please contact support@pistildata.com for assistance with adding new retailers.
             </Text>
           </div>
         </div>


                 {storeType !== 'api_sync' && (
           <div className="form-actions">
             <Button
               onClick={handleSubmit}
               disabled={isLoading || !selectedStore || !storeType}
               className={isLoading ? 'loading' : ''}
               color="brand"
             >
               {isLoading ? 'Sending...' : 'Send Invitation'}
             </Button>
           </div>
         )}
      </form>
    </StyledInviteForm>
  );
};

export default InviteForm;


